import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Check, Settings, ChevronDown } from "lucide-react";
import { formatGoal, DEFAULT_ICON, DEFAULT_COLOR } from "./workspaceModel";

const HOVER_NUDGE = { x: 2 };
const HOVER_GROW = { scale: 1.01 };
const TAP_SHRINK = { scale: 0.99 };
const CHEVRON_OPEN = { rotate: 180 };
const CHEVRON_CLOSED = { rotate: 0 };
const CHEVRON_SPIN = { duration: 0.2 };
const MENU_HIDDEN = { opacity: 0, y: -10, scale: 0.95 };
const MENU_SHOWN = { opacity: 1, y: 0, scale: 1 };
const MENU_FADE = { duration: 0.15 };

const WorkspaceIcon = ({ instance, size = "w-9 h-9 text-lg" }) => (
  <div className={`${size} rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${instance?.color || DEFAULT_COLOR}20` }}>
    {instance?.icon || DEFAULT_ICON}
  </div>
);

const WorkspaceRow = ({ instance, isCurrent, onSwitch, onOpenSettings }) => (
  <motion.button onClick={() => onSwitch(instance)} whileHover={HOVER_NUDGE}
    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isCurrent ? "bg-ism-teal/10 border border-ism-teal/30" : "hover:bg-white/5"}`}>
    <WorkspaceIcon instance={instance} />
    <div className="flex-1 text-left">
      <p className="text-white text-sm font-normal">{instance.name}</p>
      <p className="text-xs text-gray-500">{instance.onboardingComplete ? formatGoal(instance.revenueGoal) : "Setup required"}</p>
    </div>
    {isCurrent && <Check className="h-4 w-4 text-ism-teal" />}
    <div onClick={(e) => { e.stopPropagation(); onOpenSettings(instance); }} role="button" tabIndex={0}
      className="p-1.5 hover:bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
      <Settings className="h-3.5 w-3.5 text-gray-500" />
    </div>
  </motion.button>
);

// Trigger button showing the current workspace + the expandable list of all workspaces.
export const WorkspaceDropdown = ({ instances, currentInstance, isExpanded, onToggle, onSwitch, onOpenSettings, onCreate }) => (
  <div className="relative" data-testid="instance-switcher">
    <motion.button onClick={onToggle} whileHover={HOVER_GROW} whileTap={TAP_SHRINK} data-testid="instance-switcher-btn"
      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-ism-teal/30 transition-all w-full group">
      <WorkspaceIcon instance={currentInstance} size="w-10 h-10 text-xl" />
      <div className="flex-1 text-left">
        <p className="text-white font-normal text-sm">{currentInstance?.name || "Select Workspace"}</p>
        {currentInstance?.revenueGoal && <p className="text-xs text-gray-500">{formatGoal(currentInstance.revenueGoal)} goal</p>}
      </div>
      <motion.div animate={isExpanded ? CHEVRON_OPEN : CHEVRON_CLOSED} transition={CHEVRON_SPIN}>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </motion.div>
    </motion.button>

    <AnimatePresence>
      {isExpanded && (
        <motion.div initial={MENU_HIDDEN} animate={MENU_SHOWN} exit={MENU_HIDDEN} transition={MENU_FADE}
          className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-gray-800 rounded-xl shadow-xl z-50 overflow-hidden">
          <ScrollArea className="max-h-80">
            <div className="p-2">
              {instances.map((instance) => (
                <WorkspaceRow key={instance.instanceId} instance={instance} isCurrent={currentInstance?.instanceId === instance.instanceId}
                  onSwitch={onSwitch} onOpenSettings={onOpenSettings} />
              ))}
              <motion.button onClick={onCreate} whileHover={HOVER_GROW}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-ism-teal hover:bg-ism-teal/10 transition-colors mt-2 border border-dashed border-ism-teal/30">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-ism-teal/10"><Plus className="h-5 w-5" /></div>
                <span className="text-sm font-normal">New Business / Initiative</span>
              </motion.button>
            </div>
          </ScrollArea>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
